import { PaymentError, UserPaymentError } from '../../lib/error';
import logger from '../../lib/log';
import { getBraintreeInstance } from '../../lib/wallets';

const log = logger('Credit Card');

export const PAYMENT_METHOD_ID = 'creditcard';
export const PAYMENT_METHOD_NAME = 'Credit / Debit Card';
export const AUTO_COMPLETE_PURCHASE = false;
export const ICON_SET = {
  // Order matters for display
  Visa: 'payment-visa-icon.svg',
  Mastercard: 'payment-mastercard-icon.svg',
  'American Express': 'payment-amex-icon.svg',
  Discover: 'payment-discover-icon.svg',
};

let authorized = false;
let isAppWebView = false;
let isIosApp = false;

const apiUrls = {
  TOKENIZE: 'payment_methods/credit_cards',
  VALIDATE: '/ExpressMobilePaymentService/NonceCreditCard',
};
const XHR_DEFAULT_TIMEOUT = 180 * 1000;

const NOT_ACCEPTED_ERROR_MESSAGE = 'This payment type is not accepted. Please try again with a valid payment method.';

/** Identifies if the provided card number is already masked */
const isCreditCardMasked = (cardNumber) => /\*+/.test(cardNumber);

/** Validates if a BIN is restricted  */
const isValidBIN = (bin) => {
  switch (bin) {
    case '511450': // MovieCash Codes
      return false;
  }
  return true;
};

/** Masks the provided card number */
const maskCardNumber = (cardNumber) => {
  if (!cardNumber) return null;

  const maskedPrefix = cardNumber.slice(0, -4).replace(/./g, '*');
  const lastFour = cardNumber.slice(-4);

  return `${maskedPrefix}${lastFour}`;
};

const sendCardToNativeApp = ({
  cardNumber, expirationMonth, expirationYear, zipCode,
}) => {
  // Send card information to the native app
  const url = 'fandango://?op=purchaserecord'
    + `&ccnum=${cardNumber}`
    + `&ccmonth=${expirationMonth}`
    + `&ccyear=${expirationYear}`
    + `&billingaddresszipcode=${zipCode}`;
  const target = isIosApp ? '_self' : undefined;
  window.open(url, target);
};

/**
 * tokenizeCreditCard
 *
 * Tokenizes the credit card preventing our servers from directly interacting with the raw card information.
 *
 * @async
 * @param {Object} options
 * @param {String} options.cardNumber - Credit card number (can potentially be a masked number)
 * @param {String} options.cvv - The security number for the card
 * @param {String} options.expirationMonth - The card's expiration month in the format of MM
 * @param {String} options.expirationYear - The card's expiration year in the format of YY
 * @param {String} options.zipCode - The billing postal code
 *
 * @returns {Object} data
 *          {String} data.bin - The first 6 digits of the credit card, known as the bank identification number (BIN).
 *          {String} data.braintreeNonce - One-time-use token that references a payment method
 *          {String} data.cardType - The type of credit card)
 * @throws {PaymentError} An error will be thrown if a braintreeNonce cannot be generated for the provided card
 */
const tokenizeCreditCard = async ({
  cardNumber, cvv, expirationMonth, expirationYear, zipCode,
}) => {
  const braintreeInstance = await getBraintreeInstance({ paymentMethodId: PAYMENT_METHOD_ID });

  const response = await braintreeInstance.request({
    endpoint: apiUrls.TOKENIZE,
    method: 'POST',
    timeout: XHR_DEFAULT_TIMEOUT,
    data: {
      creditCard: {
        number: cardNumber,
        expirationDate: expirationMonth && expirationYear
          ? `${expirationMonth}/${expirationYear}` : null,
        billingAddress: {
          postalCode: zipCode,
        },
        cvv: cvv, // eslint-disable-line object-shorthand
      },
    },
  });

  log('tokenize response', response); // https://developers.braintreepayments.com/reference/response/payment-method-nonce/php

  const { nonce: braintreeNonce, details, binData } = _.get(response, 'creditCards[0]') || {};
  const { bin, cardType } = details || {};
  const { countryOfIssuance: countryCode } = binData || {};
  if (!braintreeNonce) throw new PaymentError('Unable to tokenize the credit card');

  return {
    bin,
    braintreeNonce,
    cardType,
    countryCode,
  };
};

export const init = (options) => {
  isAppWebView = options.isAppWebView;
  isIosApp = options.isIosApp;
};

/**
 * activate
 *
 * Identifies the card on file as being an active / verified payment method.
 *
 * NOTE: This should ONLY be called when we are sure there is a valid card on file that is NOT expired.
 */
export const activate = () => {
  authorized = true;
};

/**
 * submit
 *
 * Adds (or updates) the credit card to use for the transaction.
 * NOTE: Client-side validation should be executed *before* calling this.
 *
 * @async
 * @param {Object} options
 * @param {String} options.cardNumber - Credit card number (can potentially be a masked number)
 * @param {String} options.cvv - The security number for the card
 * @param {String} options.expirationMonth - The card's expiration month in the format of MM
 * @param {String} options.expirationYear - The card's expiration year in the format of YY
 * @param {String} options.zipCode - The billing postal code
 * @param {Boolean=} options.saveCard - Flag to indicate if the card should be saved for future transactions
 *
 * @returns {Object} data
 *          {String} data.MaskedNumber -
 *          {String} data.CardType -
 *          {String} data.PostalCode -
 *          {String} data.ExpireMonth -
 *          {String} data.ExpireYear -
 *          {Boolean} data.CanCompletePurchase -
 *          {Boolean} data.RequiresUpdate - If the cc number remains the same but the zip code or the
 *                      expiration date do not match with the existing info, then we want to update
 *                      the braintree token and fandango db
 * @throws {PaymentError}
 */
export const submit = async ({
  cardNumber, cvv, expirationMonth, expirationYear, zipCode, saveCard,
}) => {
  const cardInfo = {
    BraintreeNonce: null,
    MaskedNumber: maskCardNumber(cardNumber),
    ExpirationMonth: expirationMonth,
    ExpirationYear: expirationYear,
    BillingZip: zipCode,
    Cvv: cvv,
    SaveCreditCardToPhone: saveCard,
  };

  if (!isCreditCardMasked(cardNumber)) {
    // New cards need to be tokenized first

    const options = {
      cardNumber, cvv, expirationMonth, expirationYear, zipCode,
    };

    const [data, secondNonceData] = await Promise.all([tokenizeCreditCard(options), tokenizeCreditCard(options)]);
    const {
      bin,
      braintreeNonce,
      cardType,
      countryCode,
    } = data;

    if (!isValidBIN(bin)) {
      throw new UserPaymentError(NOT_ACCEPTED_ERROR_MESSAGE);
    }

    cardInfo.BraintreeNonce = braintreeNonce;
    cardInfo.BraintreeNonce2 = secondNonceData.braintreeNonce;
    cardInfo.BillingCountryCode = countryCode;
    cardInfo.CardType = cardType === 'American Express' ? 'Amex' : cardType;
  }

  let error;
  let response;
  await $.ajax({
    type: 'POST',
    timeout: XHR_DEFAULT_TIMEOUT,
    url: apiUrls.VALIDATE,
    dataType: 'json',
    contentType: 'application/json',
    data: JSON.stringify(cardInfo),
    success: (res) => {
      log('validation success', res);
      const data = _.get(res, 'ReturnData') || {};
      const errorMessages = _.get(res, 'ErrorMessages');
      if (!_.isEmpty(errorMessages)) {
        const errorStatus = _.get(res, 'Status');
        error = new UserPaymentError(errorMessages, errorStatus);
        return;
      }
      response = _.pick(data, [
        'CanCompletePurchase',
        'ClaimCodeInfo',
        'PaymentError',
        'MaskedNumber',
        'CardType',
        'PostalCode',
        'ExpireMonth',
        'ExpireYear',
        'RequiresUpdate',
        'OrderSummary',
      ]);
    },
    error: (xhr) => {
      const errors = _.get(xhr, 'responseJSON.errors');
      error = new PaymentError(errors);
    },
  });

  if (error) throw error;

  authorized = true;

  if (saveCard && isAppWebView) {
    sendCardToNativeApp({
      cardNumber, expirationMonth, expirationYear, zipCode,
    });
  }

  return response;
};

export const isAuthorized = () => authorized;

export default PAYMENT_METHOD_ID;
